import { EhrConnector } from "@elation/api-client/src/stitch-web/models/ehrConnector";
import { PatientFromIframe } from "./store";

export const IFrameMessageType = {
  EL8_STITCH_IFRAME_READY: "EL8_STITCH_IFRAME_READY",
  EL8_STITCH_IFRAME_REQUEST_AUTH_TOKEN: "EL8_STITCH_IFRAME_REQUEST_AUTH_TOKEN",
  EL8_STITCH_PARENT_INIT: "EL8_STITCH_PARENT_INIT",
  EL8_STITCH_PARENT_SEND_AUTH_TOKEN: "EL8_STITCH_PARENT_SEND_AUTH_TOKEN",
} as const;

export interface TranscriptionStartedPayload {
  sessionId: string;
  practiceId: number;
  encounterId: number;
  physicianId: number;
}

export interface TranscriptionStoppedPayload {
  sessionId: string;
  practiceId: number;
  encounterId: number;
  physicianId: number;
}

export interface IFrameMessage<Payload = unknown> {
  type: (typeof IFrameMessageType)[keyof typeof IFrameMessageType];
  payload: Payload;
}

export interface IFrameMessageEvent<Payload = unknown> extends MessageEvent {
  data: IFrameMessage<Payload>;
}

/**
 * Notification message to the parent iframe that the iframe is ready to receive messages.
 */
export function iframeReadyMessage() {
  console.log(
    `[stitch-iframe] sending ${IFrameMessageType.EL8_STITCH_IFRAME_READY} message to parent`,
  );
  return {
    type: IFrameMessageType.EL8_STITCH_IFRAME_READY,
  };
}

/**
 * Provides context to the iframe from the parent window.
 *
 * @param email - The email of the current user.
 * @param patient - The patient search request.
 */
export function parentInitMessage(
  email: string,
  patient: PatientFromIframe,
  ehr: EhrConnector,
) {
  console.log(
    `[stitch-iframe] sending ${IFrameMessageType.EL8_STITCH_PARENT_INIT} message to parent`,
  );

  return {
    type: IFrameMessageType.EL8_STITCH_PARENT_INIT,
    payload: { user_email: email, patient, ehr },
  };
}

export function iframeRequestAuthTokenMessage() {
  return {
    type: IFrameMessageType.EL8_STITCH_IFRAME_REQUEST_AUTH_TOKEN,
  };
}

/**
 * Provides authentication headers to the iframe from the parent window.
 */
export function parentSendAuthTokenMessage({ token }: { token: string }) {
  return {
    type: IFrameMessageType.EL8_STITCH_PARENT_SEND_AUTH_TOKEN,
    payload: { token },
  };
}

export function isIframeReadyMessage(
  event: MessageEvent,
): event is IFrameMessageEvent {
  return event.data?.type === IFrameMessageType.EL8_STITCH_IFRAME_READY;
}

export function isParentInitMessage(
  event: MessageEvent,
): event is IFrameMessageEvent<{
  user_email: string;
  patient: PatientFromIframe;
  ehr: EhrConnector;
}> {
  return event.data?.type === IFrameMessageType.EL8_STITCH_PARENT_INIT;
}

export function isParentSendAuthTokenMessage(
  event: MessageEvent,
): event is IFrameMessageEvent<{ token: string }> {
  return (
    event.data?.type === IFrameMessageType.EL8_STITCH_PARENT_SEND_AUTH_TOKEN
  );
}

export function isIframeRequestAuthTokenMessage(
  event: MessageEvent,
): event is IFrameMessageEvent {
  return (
    event.data?.type === IFrameMessageType.EL8_STITCH_IFRAME_REQUEST_AUTH_TOKEN
  );
}

export function isWhitelistedParentDomain(domain: string): boolean {
  if (domain.includes("localhost") || domain.endsWith(".localhost")) {
    return window.location.hostname.includes("localhost");
  }

  return (
    domain.endsWith("elationemr.com") ||
    domain.endsWith("el8.io") ||
    domain.endsWith("el8-dev-services.internal") ||
    domain.endsWith("el8-stage-services.internal") ||
    domain.endsWith("el8-sandbox-services.internal") ||
    domain.endsWith("el8-production-services.internal")
  );
}

// 15s gives the parent time to actually mint a token (network round-trip
// to its identity provider) when wired to an active `onRefreshAuthToken`
// callback. The previous 5s budget was tight enough that real refresh
// calls — not just echoes of an in-memory prop — would race the timeout
// and resolve with an empty token, masquerading as auth failure.
const REQUEST_AUTH_TOKEN_TIMEOUT_MS = 15_000;

export function requestAuthTokenFromParent(): Promise<{
  token: string;
}> {
  return new Promise((resolve) => {
    const timeout = setTimeout(() => {
      window.removeEventListener("message", handleMessage);
      resolve({ token: "" });
    }, REQUEST_AUTH_TOKEN_TIMEOUT_MS);

    const handleMessage = (event: MessageEvent) => {
      const hostname = new URL(event.origin).hostname;

      if (!isWhitelistedParentDomain(hostname)) {
        console.error("Invalid parent domain", hostname);
        return;
      }

      if (!isParentSendAuthTokenMessage(event)) return;

      clearTimeout(timeout);

      window.removeEventListener("message", handleMessage);

      resolve(event.data.payload);
    };

    window.addEventListener("message", handleMessage);

    window.parent.postMessage(iframeRequestAuthTokenMessage(), "*");
  });
}
